import { isNumber, uniqBy } from 'lodash';

import {
  CompensationDimension,
  type CompensationFilters,
  GET_TOP_N,
  type GetTopNQuery,
  PositionsEntityKind,
} from '@revelio/data-access';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { getTopNFilters } from '../../filters/utils/get-top-n-filters';
import { getDim1 } from '../../filters/utils/getDim1';
import {
  effectiveFiltersWithSinglePrimaryEntity,
  useChartPrimaryFilter,
} from '../../hooks/useChartPrimaryFilter';
import { useChart } from '../Chart/ChartProvider';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import { PlotFilterType } from '../PageConfigProvider/types/item';
import { TOP_N_LIMIT } from './constants';
import type { DataProviderChildren } from './types';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';

type TopNDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  children: DataProviderChildren<TopEntity[] | undefined>;
};

export type SupportedSecondaryDimensions = Extract<
  SecondaryDimensionValues,
  'company' | 'geography'
  // | 'role'
>;

const getDim2 = (
  secondaryDimension: SupportedSecondaryDimensions
): CompensationDimension => {
  switch (secondaryDimension) {
    case 'company':
      return CompensationDimension.Company;
    case 'geography':
      return CompensationDimension.Geography;
    /** Role not yet supported since backend doesn't allow role_k50, role_k500 etc. as entities */
    // case 'role':
    //   return CompensationDimension.Role;
  }
};

export const TopNDataProvider = ({
  primaryDimension,
  secondaryDimension,
  children,
}: TopNDataProviderProps) => {
  const dim1 = getDim1(primaryDimension);
  const dim2 = getDim2(secondaryDimension);

  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });

  const { item } = useChart();
  const chartPrimaryFilter = useChartPrimaryFilter({ primaryDimension });

  const effectiveFiltersWithSingleEntity =
    effectiveFiltersWithSinglePrimaryEntity({
      item,
      effectiveFilters,
      chartPrimaryFilter,
    });

  const filters = getTopNFilters(effectiveFiltersWithSingleEntity);

  const plotFilters = item?.plot?.plotFilters;

  const [{ data, status }] = useUrqlQueryWithStatus({
    query: GET_TOP_N,
    variables: {
      dim1,
      dim2,
      filters,
    },
  });

  const topNByHeadcountFilter = plotFilters?.find(
    (f) => f.type === PlotFilterType.TOP_N_SAMPLE_SIZE
  )?.value;

  const topNFilterValue = plotFilters?.find(
    (f) => f.type === PlotFilterType.TOP_N
  )?.value;
  const entityLimit =
    isNumber(topNFilterValue) &&
    Number.isFinite(topNFilterValue) &&
    topNFilterValue > 0
      ? topNFilterValue
      : TOP_N_LIMIT;

  const filteredData =
    isNumber(topNByHeadcountFilter) && data?.compensation
      ? filterTopNByHeadcount(data?.compensation, topNByHeadcountFilter)
      : data?.compensation;

  const topEntities = filteredData
    ? getTopEntities({
        data: filteredData,
        secondaryDimension,
        filters,
      })?.slice(0, entityLimit)
    : undefined;

  return children({ data: topEntities, status });
};

const filterTopNByHeadcount = (
  data: GetTopNQuery['compensation'],
  topNByHeadcountFilter: number
) =>
  data?.map((d) => ({
    ...d,
    category: d?.category
      ?.sort((a, b) => (b?.metrics?.count ?? 0) - (a?.metrics?.count ?? 0))
      .slice(0, topNByHeadcountFilter),
  }));

export type TopEntity = {
  EntID: number;
  EntType: PositionsEntityKind;
};

export const getTopEntities = ({
  data,
  secondaryDimension,
  filters,
}: {
  data: GetTopNQuery['compensation'];
  secondaryDimension: SupportedSecondaryDimensions;
  filters: CompensationFilters;
}): TopEntity[] => {
  switch (secondaryDimension) {
    case 'company': {
      const topEntities =
        data
          ?.flatMap((d) => d?.category)
          ?.map((category): TopEntity | null => {
            if (!category?.metadata?.id) return null;
            return {
              EntID: category?.metadata?.id,
              EntType: PositionsEntityKind.Company,
            };
          })
          .filter((entity) => entity !== null) || [];

      return uniqBy(topEntities, 'EntID');
    }

    case 'geography': {
      const hasRegionSelections =
        filters.region?.length && filters.region.length > 0;
      const hasCountrySelections =
        filters.country?.length && filters.country.length > 0;
      const hasMsaSelections = filters.msa?.length && filters.msa.length > 0;
      const hasCitySelections = filters.city?.length && filters.city.length > 0;

      const categories = data?.find(
        (d) => d?.dimension === CompensationDimension.Geography
      )?.category;
      if (!categories) return [];

      const EntType = (() => {
        if (hasCitySelections) return PositionsEntityKind.City;
        if (hasMsaSelections) return PositionsEntityKind.City;
        if (hasCountrySelections) return PositionsEntityKind.Msa;
        if (hasRegionSelections) return PositionsEntityKind.Country;
        return PositionsEntityKind.Region;
      })();

      return (
        categories
          ?.map((category): TopEntity | null => {
            if (!category?.metadata?.id) return null;
            return {
              EntID: category?.metadata?.id,
              EntType,
            };
          })
          .filter((entity) => entity !== null) || []
      );
    }

    // case 'role': {
    //   const customRole = filters.custom_role;
    //   const hasRoleK7Selections = customRole?.entities?.some((entity) => entity?.levelId === 'k7');
    //   const hasRoleK150Selections = customRole?.entities?.some((entity) => entity?.levelId === 'k150');
    //   const hasRoleK1500Selections = customRole?.entities?.some((entity) => entity?.levelId === 'k1500');
    //   const hasRoleK15000Selections = customRole?.entities?.some((entity) => entity?.levelId === 'k15000');

    //   const EntType = (() => {
    //     if (hasRoleK15000Selections) return PositionsEntityKind.RoleK15000;
    //     if (hasRoleK1500Selections) return PositionsEntityKind.R
    //     // if (hasRoleK7Selections) return PositionsEntityKind.Role;
    //     // if (hasRoleK150Selections) return PositionsEntityKind.Role;
    //   })();
    // }
  }
};
